import cn from 'classnames'
import { useCallback, useMemo } from 'react'

import { SegmentedControl } from 'cng-common/components/segmentedControl'
import { TSegmentConrolCommon } from 'cng-common/components/segmentedControl/models'
import { Translation } from 'cng-common/features/translations/components/translation'
import { Tooltip } from 'core-components/library/tooltip'

import styles from './RowButtons.module.scss'

interface IRowButtonsProps<T> {
  value: T
  options: T[]
  generateTranslationKey: (value: T, index: number) => string
  generateTooltipTranslationKey?: (value: T) => string
  containerClassName?: string
  buttonClassName?: string
  onOptionClick: (newValue: T) => void
  isDisabled?: boolean
  disableOptionTranslation?: boolean
  size?: TSegmentConrolCommon['size']
  background?: TSegmentConrolCommon['backgroudn']
}

export const RowButtons = <T,>({
  value,
  options,
  generateTranslationKey,
  generateTooltipTranslationKey,
  onOptionClick,
  containerClassName,
  isDisabled,
  disableOptionTranslation,
  size,
  background,
}: IRowButtonsProps<T>) => {
  const selectedIndex = useMemo(() => {
    return options.findIndex((el) => el === value) || 0
  }, [options, value])

  const onChange = useCallback(
    (index: number) => {
      const option = options[index]
      onOptionClick(option)
    },
    [onOptionClick, options]
  )

  return (
    <div className={cn(styles.rowContainer, containerClassName)}>
      <SegmentedControl
        selectedItem={selectedIndex}
        backgroudn={background || 'bg-default'}
        variant="default"
        onItemSelect={onChange}
        isDisabled={isDisabled}
        size={size}
      >
        {options.map((option, index) => {
          const translationKey = generateTranslationKey(option, index)
          const isTooltipEnabled = !!generateTooltipTranslationKey

          return (
            <Tooltip
              key={`rowButton-${index}`}
              disabled={!isTooltipEnabled}
              tipContent={() => {
                if (isTooltipEnabled) {
                  return <Translation text={generateTooltipTranslationKey(option)} />
                }

                return <></>
              }}
            >
              <SegmentedControl.Item key={translationKey}>
                {disableOptionTranslation ? <>{translationKey}</> : translationKey}
              </SegmentedControl.Item>
            </Tooltip>
          )
        })}
      </SegmentedControl>
    </div>
  )
}
